/**
 * DATEVSettingsCard — Admin-Karte für die DATEV-LODAS-Lohnübergabe.
 *
 * Speichert Mandantennummer + Beraternummer + Lohnarten-Mapping zentral.
 * Wird beim Export aus der Zeiterfassung verwendet (kann dort einmalig
 * überschrieben werden).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileSpreadsheet, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const LOHNART_FIELDS = [
  { key: 'regular',     label: 'Lohn / Gehalt (regulär)' },
  { key: 'overtime',    label: 'Mehrarbeit / Überstunden' },
  { key: 'sick',        label: 'Krankheit (K)' },
  { key: 'child_sick',  label: 'Kind krank (KK)' },
  { key: 'vacation',    label: 'Urlaub' },
  { key: 'holiday',     label: 'Feiertagsarbeit' },
  { key: 'sunday',      label: 'Sonntagsarbeit' },
  { key: 'parental',    label: 'Elternzeit (EZ)' },
  { key: 'ko_day',      label: 'Krank ohne Krankenschein (KO)' },
];

const DEFAULTS = {
  regular: '100', overtime: '200', sick: '030', child_sick: '035',
  vacation: '040', holiday: '050', sunday: '060', parental: '045', ko_day: '031',
};

export const DATEVSettingsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/datev/settings')
      .then((res) => setData(res.data))
      .catch(() => { /* admin only */ })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border-border/60">
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          DATEV-Einstellungen werden geladen…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const setField = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setLohnart = (k, v) =>
    setData((p) => ({ ...p, lohnarten: { ...p.lohnarten, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/datev/settings', data);
      setData(res.data);
      toast.success('DATEV-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const resetLohnarten = () =>
    setData((p) => ({ ...p, lohnarten: { ...DEFAULTS } }));

  return (
    <Card className="border-border/60" data-testid="datev-settings-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          DATEV-Export-Einstellungen (LODAS)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Stammdaten für die Lohnübergabe. Werte erhältst du vom Steuerberater
          oder vom Lohnbüro. Die Lohnarten kannst du auf das mandantenspezifische
          Schema deines Lohnbüros anpassen.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="datev-mandant">Mandantennummer</Label>
            <Input
              id="datev-mandant"
              value={data.mandant || ''}
              onChange={(e) => setField('mandant', e.target.value)}
              placeholder="z.B. 12345"
              className="font-mono"
              data-testid="datev-mandant"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="datev-berater">Beraternummer</Label>
            <Input
              id="datev-berater"
              value={data.berater || ''}
              onChange={(e) => setField('berater', e.target.value)}
              placeholder="z.B. 1234567"
              className="font-mono"
              data-testid="datev-berater"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="datev-hours">Std/Tag (Standard)</Label>
            <Input
              id="datev-hours"
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={data.default_hours_per_day ?? 8}
              onChange={(e) => setField('default_hours_per_day', parseFloat(e.target.value) || 8)}
              data-testid="datev-hours"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Lohnarten-Mapping</Label>
            <Button type="button" variant="ghost" size="sm" onClick={resetLohnarten} className="text-xs">
              <RotateCcw className="h-3 w-3 mr-1" />
              DATEV-Standard
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LOHNART_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-2">
                <Label className="text-sm flex-1 min-w-0 truncate" title={f.label}>{f.label}</Label>
                <Input
                  className="w-24 font-mono"
                  value={data.lohnarten?.[f.key] || ''}
                  onChange={(e) => setLohnart(f.key, e.target.value)}
                  placeholder={DEFAULTS[f.key]}
                  data-testid={`datev-lohnart-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-border">
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="datev-settings-save">
            {saving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichere…</>) : 'Speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
